import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Award, Users, Target, Heart } from "lucide-react";
import useDocumentMeta from "../lib/useDocumentMeta";
import FooterCTA from "../components/FooterCTA";
import { CONSULT_LINK } from "../lib/constants";

const values = [
    { icon: Target, title: "Strategy First", desc: "Every visual is anchored in a clear business outcome." },
    { icon: Award, title: "Craft Obsession", desc: "Pixel‑perfect details, considered typography, intentional color." },
    { icon: Users, title: "Senior Hands", desc: "Real designers — not handed off to juniors after the brief." },
    { icon: Heart, title: "Long Partnerships", desc: "We stay involved long after launch to grow with the brand." },
];

const AboutPage = () => {
    useDocumentMeta({
        title: "About Raj Graphics — Design & Branding Studio in Nagpur",
        description: "A graphic design and branding studio led by senior designers, based in Nagpur, Maharashtra — serving businesses across India.",
        canonical: "/about",
    });

    return (
        <main data-testid="about-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-gold/[0.07] blur-[120px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        About the Studio
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="about-headline"
                    >
                        A small studio with <span className="gold-gradient-text">senior craft</span> &amp; sharp instincts.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-7 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Raj Graphics is a Nagpur‑based design and branding studio. We help founders,
                        local businesses, and growing brands look the part — through identity,
                        signage, packaging and marketing assets that move the needle.
                    </motion.p>
                </div>
            </section>

            {/* Story */}
            <section className="relative py-20 md:py-28 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            Our Story
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl">
                            Eight years of designing brands that earn attention.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5 font-inter text-zinc-400 text-base leading-relaxed">
                        <p>
                            What started as a one‑desk operation handling logo work for neighbourhood
                            businesses has grown into a full‑stack studio practicing identity, signage,
                            packaging and digital design — all under one roof.
                        </p>
                        <p>
                            We're hands‑on by design. The same person who scopes your project sketches
                            the first concept and oversees the final print run. Nothing slips through
                            the cracks because nothing leaves our table without a careful look.
                        </p>
                        <p>
                            Today we serve hundreds of businesses across Maharashtra and beyond — from
                            local sweet shops and retail brands to D2C startups and corporate offices.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="relative py-20 md:py-28 border-t border-white/5 bg-[#080808]">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-2xl mb-14">
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            What We Believe
                        </div>
                        <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
                            Principles we ship by.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                        {values.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.5, delay: i * 0.07 }}
                                    data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="bg-[#0c0c0c] border border-white/5 rounded-2xl p-7 hover:border-gold/30 transition-colors"
                                >
                                    <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center mb-5">
                                        <Icon size={18} className="text-gold" />
                                    </div>
                                    <h3 className="font-poppins font-medium text-white text-lg">{v.title}</h3>
                                    <p className="mt-2 font-inter text-sm text-zinc-400 leading-relaxed">{v.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="relative py-20 md:py-24 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        { k: "200+", v: "Brands Crafted" },
                        { k: "8 yrs", v: "Studio Experience" },
                        { k: "50+", v: "Signage Installs" },
                        { k: "100%", v: "Hands‑on Delivery" },
                    ].map((s) => (
                        <div key={s.v} className="border-l border-white/10 pl-5">
                            <div className="font-poppins font-semibold text-white text-3xl sm:text-4xl">{s.k}</div>
                            <div className="mt-1 font-inter text-xs uppercase tracking-[0.2em] text-zinc-500">{s.v}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA strip */}
            <section className="relative py-16 md:py-20 border-t border-white/5 bg-[#080808]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <h3 className="font-poppins font-medium text-white text-2xl sm:text-3xl max-w-xl">
                        Got a brand in mind? Let's talk through it.
                    </h3>
                    <a
                        href={CONSULT_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-contact-cta"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter px-7 py-3.5 rounded-full transition-colors"
                    >
                        Book Free Consultation <ArrowRight size={18} />
                    </a>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default AboutPage;
